/**
 * Serviço para registro de atividades de usuários
 * Captura automaticamente o User-Agent e tenta obter o IP do usuário
 */
import { authApi } from './BaseApiService';

/**
 * Tenta obter o IP do usuário usando um serviço externo
 * @returns {Promise<string|null>} IP do usuário ou null se não for possível
 */
async function getUserIP() {
  try {
    // Esta função usa um serviço externo para determinar o IP
    // NOTA: Em um ambiente de produção, considere implementar isso no backend
    // para maior precisão e segurança
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.error('[ActivityLogService] Erro ao obter IP do usuário:', error);
    return null;
  }
}

/**
 * Obtém informações do navegador e dispositivo do usuário
 * @returns {string} String com informações do dispositivo
 */
function getUserAgent() {
  return navigator.userAgent;
}

/**
 * Registra uma atividade do usuário
 * @param {string} clientId - ID do cliente
 * @param {string} activityType - Tipo da atividade (login, logout, search, etc.)
 * @param {string} description - Descrição da atividade
 * @param {Object} options - Opções adicionais
 * @returns {Promise<Object>} - Resposta da API
 */
export async function logUserActivity(clientId, activityType, description, options = {}) {
  try {
    // Verifica se um ID de cliente foi fornecido
    if (!clientId) {
      throw new Error('ID do cliente é necessário para registrar atividade');
    }

    // Prepara os dados para a requisição
    const logData = {
      client_id: clientId,
      activity_type: activityType,
      description: description,
    };

    // Captura o user-agent automaticamente se não fornecido
    if (!options.user_agent) {
      logData.user_agent = getUserAgent();
    } else {
      logData.user_agent = options.user_agent;
    }

    // Tenta obter o IP do usuário se não fornecido
    if (!options.ip_address) {
      try {
        const ip = await getUserIP();
        if (ip) {
          logData.ip_address = ip;
        }
      } catch (ipError) {
        // Se não conseguir obter o IP, prossegue sem ele
        // O backend vai tentar capturar a partir da requisição
        console.warn('[ActivityLogService] Não foi possível obter o IP do usuário:', ipError);
      }
    } else {
      logData.ip_address = options.ip_address;
    }

    // Envia a requisição para a API
    const response = await authApi.post('/api/v1/activity/log', logData);
    return response.data;
  } catch (error) {
    console.error('[ActivityLogService] Erro ao registrar atividade:', error);
    // eslint-disable-next-line preserve-caught-error
    throw new Error(`Falha ao registrar atividade: ${error.message}`);
  }
}

/**
 * Registra um login de usuário
 * @param {string} clientId - ID do cliente
 * @param {string} email - Email do cliente
 * @returns {Promise<Object>} - Resposta da API
 */
export async function logUserLogin(clientId, email) {
  return logUserActivity(clientId, 'login', `Login bem-sucedido para o usuário ${email}`);
}

/**
 * Registra um logout de usuário
 * @param {string} clientId - ID do cliente
 * @param {string} email - Email do cliente
 * @returns {Promise<Object>} - Resposta da API
 */
export async function logUserLogout(clientId, email) {
  return logUserActivity(clientId, 'logout', `Logout do usuário ${email}`);
}

/**
 * Registra uma busca realizada pelo usuário
 * @param {string} clientId - ID do cliente
 * @param {string|Object} searchQuery - Termo de busca ou parâmetros de busca
 * @returns {Promise<Object>} - Resposta da API
 */
export async function logUserSearch(clientId, searchQuery) {
  // Determina o tipo de busca com base nos parâmetros
  let _searchType = 'unknown';
  // eslint-disable-next-line no-useless-assignment
  let searchDescription = '';

  // Normaliza para string quando for objeto (mantendo apenas os valores relevantes)
  if (typeof searchQuery === 'object') {
    if (searchQuery.latitude && searchQuery.longitude) {
      _searchType = 'coordinates';
      searchDescription = `Busca por coordenadas: [${searchQuery.latitude}, ${searchQuery.longitude}]`;
    } else if (searchQuery.cod_imovel) {
      _searchType = 'car';
      searchDescription = `Busca por código CAR: ${searchQuery.cod_imovel}`;
    } else if (searchQuery.codigo_imo) {
      _searchType = 'sigef';
      searchDescription = `Busca por código SIGEF: ${searchQuery.codigo_imo}`;
    } else if (searchQuery.parcela_co) {
      _searchType = 'parcela';
      searchDescription = `Busca por parcela: ${searchQuery.parcela_co}`;
    } else {
      // Converte objeto para string legível
      const keys = Object.keys(searchQuery);
      if (keys.length > 0) {
        searchDescription = `Busca com parâmetros: ${JSON.stringify(searchQuery)}`;
      } else {
        searchDescription = 'Busca sem parâmetros';
      }
    }
  } else {
    // Caso seja uma string direta
    searchDescription = `Busca por termo: ${searchQuery}`;
  }

  return logUserActivity(clientId, 'search', searchDescription);
}

/**
 * Registra a visualização de uma propriedade pelo usuário
 * @param {string} clientId - ID do cliente
 * @param {Object} propertyData - Dados da propriedade visualizada
 * @returns {Promise<Object>} - Resposta da API
 */
export async function logPropertyView(clientId, propertyData) {
  // Determinar o código a ser usado no log: prioridade para SIGEF, depois CAR
  let propertyCode = null;
  let propertyType = null;

  try {
    // Tenta obter código SIGEF primeiro (tem prioridade)
    if (propertyData?.sigef) {
      // Tenta obter de diferentes estruturas possíveis
      const sigefCode =
        // Array de SIGEF
        (Array.isArray(propertyData.sigef) && propertyData.sigef[0]?.codigo_imo) ||
        // Objeto SIGEF direto
        propertyData.sigef?.codigo_imo ||
        // Parcelas dentro de SIGEF
        (propertyData.sigef?.parcels && propertyData.sigef.parcels[0]?.codigo_imo) ||
        // Array de parcelas
        (Array.isArray(propertyData.sigef?.parcels) && propertyData.sigef.parcels[0]?.codigo_imo);

      if (sigefCode) {
        propertyCode = sigefCode;
        propertyType = 'SIGEF';
      }
    }

    // Se não encontrou SIGEF, tenta CAR
    if (!propertyCode && propertyData?.car) {
      // Tenta obter de diferentes estruturas possíveis
      const carCode =
        // Array de CAR
        (Array.isArray(propertyData.car) && propertyData.car[0]?.cod_imovel) ||
        // Objeto CAR direto
        propertyData.car?.cod_imovel ||
        // Temas dentro de CAR
        (propertyData.car?.themes && propertyData.car.themes[0]?.cod_imovel);

      if (carCode) {
        propertyCode = carCode;
        propertyType = 'CAR';
      }
    }

    // Se tiver properties, verifica nessa estrutura também
    if (!propertyCode && propertyData?.properties) {
      // Tenta SIGEF primeiro em properties
      if (propertyData.properties.sigef) {
        const sigefCode =
          (Array.isArray(propertyData.properties.sigef) &&
            propertyData.properties.sigef[0]?.codigo_imo) ||
          propertyData.properties.sigef?.codigo_imo;

        if (sigefCode) {
          propertyCode = sigefCode;
          propertyType = 'SIGEF';
        }
      }

      // Se não encontrou SIGEF em properties, tenta CAR
      if (!propertyCode && propertyData.properties.car) {
        const carCode =
          (Array.isArray(propertyData.properties.car) &&
            propertyData.properties.car[0]?.cod_imovel) ||
          propertyData.properties.car?.cod_imovel;

        if (carCode) {
          propertyCode = carCode;
          propertyType = 'CAR';
        }
      }
    }

    // Se ainda não encontrou, procura em _contextData
    if (!propertyCode && propertyData?._contextData) {
      if (propertyData._contextData.codigo_imo) {
        propertyCode = propertyData._contextData.codigo_imo;
        propertyType = 'SIGEF';
      } else if (propertyData._contextData.cod_imovel) {
        propertyCode = propertyData._contextData.cod_imovel;
        propertyType = 'CAR';
      }
    }

    // Se não encontrou nenhum código, usa o filename como último recurso
    if (
      !propertyCode &&
      (propertyData?._metadata?.file_id || propertyData?._contextData?.filename)
    ) {
      const filename =
        propertyData._metadata?.file_id ||
        propertyData._contextData?.filename.replace(/\.json$/, '');
      propertyCode = filename;
      propertyType = 'Arquivo';
    }

    // Se mesmo assim não tiver um código, usa um valor genérico
    if (!propertyCode) {
      propertyCode = 'sem_codigo';
      propertyType = 'Desconhecido';
    }

    // Formata a descrição com base no tipo
    const viewDescription = `Visualização de propriedade - ${propertyType}: ${propertyCode}`;

    // Registra a atividade
    return logUserActivity(clientId, 'visualizar', viewDescription);
  } catch (error) {
    console.error('[ActivityLogService] Erro ao registrar visualização de propriedade:', error);
    // eslint-disable-next-line preserve-caught-error
    throw new Error(`Falha ao registrar visualização: ${error.message}`);
  }
}
